import { FolderOutlined } from '@mui/icons-material'
import { Autocomplete, Box, Checkbox, FormControlLabel, TextField, Typography, styled, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { MainButton } from 'src/components/MainButton'
import { ExistingGroupOption } from 'src/generated-types/configure-positions'
import { IPositionDrawing } from 'src/generated-types/position-drawing'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { PanelBand } from './ConfigurePositionsPanel.style'
import { filterGroups } from './existingGroups.action'

interface AssignDrawingFooterProps {
  pickedDrawing?: IPositionDrawing
  grouping: boolean
  groupName: string
  suggestedGroupName: string
  existingGroups: ExistingGroupOption[]
  lockedGroup?: ExistingGroupOption
  saving: boolean
  renameOnly: boolean
  canRemove: boolean
  onGrouping: (value: boolean, suggestedName: string) => void
  onGroupName: (value: string) => void
  onAssign: () => void
  onRemoveSelected: () => void
}

const AssignDrawingFooter: React.FC<AssignDrawingFooterProps> = ({
  pickedDrawing,
  grouping,
  groupName,
  suggestedGroupName,
  existingGroups,
  lockedGroup,
  saving,
  renameOnly,
  canRemove,
  onGrouping,
  onGroupName,
  onAssign,
  onRemoveSelected,
}) => {
  const theme = useTheme()
  const { t } = useTranslation('position')
  const primary = getThemeColor(theme, MUIThemeColors.primaryMain)

  const assignLabel = () => {
    if (!pickedDrawing) return t('configure-select-drawing')
    if (renameOnly) return t('configure-update-group-name')
    return t('configure-assign', { drawing: pickedDrawing.drawingNumber })
  }

  return (
    <PanelBand dividerEdge="top">
      <GroupingToggle
        control={
          <Checkbox
            checked={grouping}
            onChange={event => onGrouping(event.target.checked, suggestedGroupName)}
            sx={{ p: 0.5, color: primary, '&.Mui-checked': { color: primary } }}
          />
        }
        label={String(t('configure-save-as-group'))}
      />
      {grouping && (
        <>
          <GroupNameField<ExistingGroupOption, false, false, true>
            freeSolo
            fullWidth
            size="small"
            openOnFocus
            options={existingGroups}
            inputValue={groupName}
            onInputChange={(_, value) => onGroupName(value)}
            getOptionLabel={option => (typeof option === 'string' ? option : option.name)}
            filterOptions={(options, state) => filterGroups(options, state.inputValue, suggestedGroupName)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
              <GroupOption component="li" {...props} key={option.id}>
                <FolderOutlined sx={{ fontSize: 16 }} htmlColor={getThemeColor(theme, MUIThemeColors.secondaryDark)} />
                <GroupOptionName>{option.name}</GroupOptionName>
                <GroupOptionDrawing>{option.drawingNumber}</GroupOptionDrawing>
              </GroupOption>
            )}
            renderInput={params => <TextField {...params} label={t('group-name')} />}
          />
          {lockedGroup && (
            <MutedNote>{t('configure-adding-to-group', { drawing: lockedGroup.drawingNumber })}</MutedNote>
          )}
        </>
      )}
      <MainButton
        variant="contained"
        disabled={!pickedDrawing || saving}
        loading={saving}
        onClick={onAssign}
        sx={{ width: '100%' }}
      >
        {assignLabel()}
      </MainButton>
      {canRemove && (
        <GridActionButton
          onClick={onRemoveSelected}
          buttonText={t('remove-drawing')}
          textColor={getThemeColor(theme, MUIThemeColors.errorDark)}
          buttonTextProps={{ fontSize: 13 }}
          border={0}
          shadowWeight={null}
          sx={{ alignSelf: 'center' }}
        />
      )}
    </PanelBand>
  )
}

export default AssignDrawingFooter

const GroupingToggle = styled(FormControlLabel)({
  margin: 0,
  '& .MuiFormControlLabel-label': { fontSize: 13, fontWeight: 600 },
})

const GroupNameField = styled(Autocomplete)(({ theme }) => ({
  '& .MuiOutlinedInput-root': { fontSize: 13 },
  '& fieldset': { borderColor: getThemeColor(theme, MUIThemeColors.inputBorder) },
})) as typeof Autocomplete

const GroupOption = styled(Box)(({ theme }) => ({ display: 'flex', gap: theme.spacing(1), fontSize: 13 })) as typeof Box

const GroupOptionName = styled('span')({ fontWeight: 600, flex: 1, minWidth: 0 })

const GroupOptionDrawing = styled('span')(({ theme }) => ({
  color: getThemeColor(theme, MUIThemeColors.secondaryDark),
  fontSize: 12,
}))

const MutedNote = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 500,
  color: getThemeColor(theme, MUIThemeColors.secondaryDark),
}))
